import logger from '../utils/logger';
import React, { useState } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  Clock,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/public/contact`, formData);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 3000);
    } catch (error) {
      logger.error('Error submitting form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'novenahfoundation@gmail.com',
      link: 'mailto:novenahfoundation@gmail.com',
    },
    {
      icon: Phone,
      title: 'Phone',
      value: '347-596-5776',
      link: 'tel:347-596-5776',
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'New York, NY',
      link: null,
    },
    {
      icon: Clock,
      title: 'Response Time',
      value: 'Within 24–48 hours',
      link: null,
    },
  ];

  return (
    <div>
      {/* Red Header Bar */}
      <div className="bg-[#BA1826] py-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Contact Us</h1>
      </div>

      {/* Contact Form & Info */}
      <section className="section-padding bg-white" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-6">
                Send Us a Message
              </h2>
              
              {isSubmitted ? (
                <div 
                  className="bg-green-50 border border-green-200 rounded-sm p-8 text-center"
                  data-testid="success-message"
                >
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-green-800 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-green-700">
                    Thank you for reaching out. We'll get back to you within 24–48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-6">
                    <div>
                      <label 
                        htmlFor="name" 
                        className="block text-sm font-medium text-[#4B5563] mb-2"
                      >
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10 transition-all"
                        placeholder="John Doe"
                        data-testid="name-input"
                      />
                    </div>

                    <div>
                      <label 
                        htmlFor="email" 
                        className="block text-sm font-medium text-[#4B5563] mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10 transition-all"
                        placeholder="john@example.com"
                        data-testid="email-input"
                      />
                    </div>

                    <div>
                      <label 
                        htmlFor="subject" 
                        className="block text-sm font-medium text-[#4B5563] mb-2"
                      >
                        Subject *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10 transition-all bg-white"
                        data-testid="subject-select"
                      >
                        <option value="">Select a subject</option>
                        <option value="general">General Inquiry</option>
                        <option value="volunteer">Volunteer Interest</option>
                        <option value="partnership">Partnership Opportunity</option>
                        <option value="donation">Donation Question</option>
                        <option value="media">Media Inquiry</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label 
                        htmlFor="message" 
                        className="block text-sm font-medium text-[#4B5563] mb-2"
                      >
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10 transition-all resize-none"
                        placeholder="Tell us how we can help..."
                        data-testid="message-input"
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="btn-primary w-full md:w-auto"
                      data-testid="submit-btn"
                    >
                      <Send className="w-4 h-4" />
                      Send Message
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-6">
                Get In Touch
              </h2>
              <p className="text-[#4B5563] mb-8">
                Have questions about our work or want to learn more about how you can 
                get involved? We're here to help.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div 
                    key={item.title} 
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${item.title.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="w-12 h-12 bg-[#FEE2E2] rounded flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#BA1826]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0A0A0A] mb-1">
                        {item.title}
                      </h4>
                      {item.link ? (
                        <a 
                          href={item.link} 
                          className="text-[#4B5563] hover:text-[#BA1826] transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-[#4B5563]">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-12 p-8 bg-[#F8F9FA] border border-[#E5E7EB]">
                <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] mb-4">
                  Before You Reach Out
                </h3>
                <ul className="space-y-3 text-sm text-[#4B5563]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#BA1826] mt-0.5 flex-shrink-0" />
                    <span>We respond to all inquiries within 24–48 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#BA1826] mt-0.5 flex-shrink-0" />
                    <span>For urgent matters, please call us directly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#BA1826] mt-0.5 flex-shrink-0" />
                    <span>Media inquiries should use the Media Inquiry subject</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="faq-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: 'How can I make a donation?',
                  answer: 'You can donate directly through our website by visiting the Donate page. We accept various payment methods and you can choose from preset amounts or enter a custom amount.',
                },
                {
                  question: 'How do I sign up to volunteer?',
                  answer: 'Fill out our contact form with "Volunteer Interest" as the subject, and we\'ll reach out with available opportunities and next steps.',
                },
                {
                  question: 'Is my donation tax-deductible?',
                  answer: 'Yes. Novena Humanitarian Foundation, Inc. is an IRS-recognized 501(c)(3) public charity (EIN 41-4097072, effective February 2, 2026). Contributions are tax-deductible to the extent permitted by law.',
                },
                {
                  question: 'How can my organization partner with Novena?',
                  answer: 'We welcome partnerships! Contact us through this form with "Partnership Opportunity" as the subject to discuss how we can work together.',
                },
              ].map((faq) => (
                <div 
                  key={faq.question} 
                  className="bg-white p-6 border border-[#E5E7EB]"
                  data-testid={`faq-${faq.question.slice(0, 20).toLowerCase().replace(/\s/g, '-')}`}
                >
                  <h4 className="font-heading font-semibold text-[#0A0A0A] mb-2">
                    {faq.question}
                  </h4>
                  <p className="text-sm text-[#4B5563]">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
